import React, { useMemo, forwardRef } from "react";

import { TEST_STEP_CONDITION_TYPE } from "@/common/constants/StepConditionType";
import {
  extractPlaceholderKey,
  resolveAttributeDisplayValue,
  resolveTestDataValue,
  STEP_PLACEHOLDER_TOKEN_REGEX,
} from "@/common/utils/stepPlaceholders";
import { findDataByKey } from "@/common/utils/utils";

interface ParseStepProps {
  step: any;
  naturalTextActions: any;
  testDataProfilesMap?: Map<number, any>;
  /** When true, text wraps across lines instead of the default single-line
   *  truncated list-row rendering. Use for detail panels/headers. */
  wrap?: boolean;
}

const TestStepResultParser = forwardRef<HTMLDivElement, ParseStepProps>(
  (
    { naturalTextActions, step, testDataProfilesMap = new Map(), wrap = false },
    ref,
  ) => {
    const resolvedTemplateId =
      step?.templateId ??
      step?.naturalTextActionId ??
      step?.natural_text_action_id;

    const template = useMemo(() => {
      if (!naturalTextActions || resolvedTemplateId == null) return null;
      return (
        naturalTextActions.find(
          (item: any) => String(item?.id) === String(resolvedTemplateId),
        ) ??
        findDataByKey(naturalTextActions, "id", resolvedTemplateId)
      );
    }, [naturalTextActions, resolvedTemplateId]);

    const parseTestStep = useMemo(() => {
      const components: React.ReactNode[] = [];
      const templateText = template?.grammar || step?.action;
      if (!templateText) return components;

      let lastIndex = 0;

      templateText.replace(
        STEP_PLACEHOLDER_TOKEN_REGEX,
        (match: string, _placeholderToken: string, offset: number) => {
          const key = extractPlaceholderKey(match);
          const isTestDataToken = match.startsWith("${");
          const isUiIdentifierToken = match === "#{uiIdentifier}";
          const isAttributeToken = match === "@{attribute}";

          if (offset > lastIndex) {
            components.push(
              <span
                key={`text-before-${offset}`}
                className="text-text-primary text-[11px] leading-normal"
                style={{ whiteSpace: wrap ? "pre-wrap" : "pre" }}
              >
                {templateText.slice(lastIndex, offset)}
              </span>,
            );
          }
          if (
            isTestDataToken &&
            key === "leftData" &&
            template?.stepActionType === TEST_STEP_CONDITION_TYPE.FOR_LOOP
          ) {
            const leftValue =
              step.forLoopCondition?.leftData?.value ||
              resolveTestDataValue(step, "leftData", "") ||
              template.data?.["testData"]?.["leftData"] ||
              "left-data";

            components.push(
              <span
                key={`left-data-${offset}`}
                className="ext-test-data-color rounded truncate text-[11px] leading-normal dark:text-[#38bdf8]"
              >
                {leftValue}
              </span>,
            );
          } else if (
            isTestDataToken &&
            key === "operator" &&
            template?.stepActionType === TEST_STEP_CONDITION_TYPE.FOR_LOOP
          ) {
            const operatorValue =
              step.forLoopCondition?.operator || "Operator Not Available";
            components.push(
              <span
                key={`operator-${offset}`}
                className="rounded truncate ext-operator-color all-small-caps text-[11px] leading-normal dark:text-[#a78bfa]"
              >
                {operatorValue}
              </span>,
            );
          } else if (
            isTestDataToken &&
            key === "rightData" &&
            template?.stepActionType === TEST_STEP_CONDITION_TYPE.FOR_LOOP
          ) {
            const rightValue =
              step.forLoopCondition?.rightData?.value ||
              resolveTestDataValue(step, "rightData", "") ||
              template.data?.["testData"]?.["rightData"] ||
              "right-data";
            components.push(
              <span
                key={`right-data-${offset}`}
                className="ext-test-data-color rounded truncate text-[11px] leading-normal dark:text-[#38bdf8]"
              >
                {rightValue}
              </span>,
            );
          } else if (isTestDataToken && key === "testDataProfile") {
            const testDataProfileValue =
              step?.forLoopCondition?.testDataProfile?.testDataName ||
              resolveTestDataValue(step, "testDataProfile", "") ||
              testDataProfilesMap.get(
                step?.forLoopCondition?.testDataProfile?.id,
              )?.testDataName ||
              "test-data";
            components.push(
              <span
                key={`testDataProfile-${offset}`}
                className="ext-test-data-color rounded truncate text-[11px] leading-normal dark:text-[#38bdf8]"
              >
                {testDataProfileValue}
              </span>,
            );
          } else if (isTestDataToken) {
            const testDataValue = resolveTestDataValue(step, key, key);
            components.push(
              <span
                key={`test-data-${offset}`}
                className="ext-test-data-color rounded truncate text-[11px] leading-normal dark:text-[#38bdf8]"
                style={{ maxWidth: "300px" }}
                title={testDataValue}
              >
                {testDataValue}
              </span>,
            );
          } else if (isUiIdentifierToken) {
            const uiIdValue =
              step?.dataMap?.uiIdentifier ?? step?.uiIdentifier ?? "element";

            components.push(
              <span
                key={`element-${offset}`}
                className="rounded truncate ext-identifier-color text-[11px] leading-normal dark:text-[#4ade80]"
              >
                {uiIdValue}
              </span>,
            );
          } else if (isAttributeToken) {
            const attrValue = resolveAttributeDisplayValue(
              step?.dataMap?.attribute ?? step?.attribute,
            );

            components.push(
              <span
                key={`attribute-${offset}`}
                className="ext-attribute-color rounded text-[11px] leading-normal dark:text-[#f59e0b]"
              >
                {attrValue}
              </span>,
            );
          }
          lastIndex = offset + match.length;
          return match;
        },
      );

      if (lastIndex < templateText.length) {
        components.push(
          <span
            key={`text-after-${lastIndex}`}
            className="text-text-primary text-[11px] leading-normal"
            style={{ whiteSpace: wrap ? "pre-wrap" : "pre" }}
          >
            {templateText.slice(lastIndex)}
          </span>,
        );
      }

      return components;
    }, [step, template, testDataProfilesMap, wrap]);

    return (
      <div
        ref={ref}
        className="w-full min-w-0 overflow-hidden"
        style={
          wrap
            ? { display: "flex", alignItems: "center", flexWrap: "wrap" }
            : {
                display: "flex",
                alignItems: "center",
                flexWrap: "nowrap",
                whiteSpace: "nowrap",
              }
        }
      >
        {parseTestStep}
      </div>
    );
  },
);

export default TestStepResultParser;
